import { PropsWithChildren, ReactNode } from 'react';
import { BoxProps } from '@mui/material';
import { FormPage } from '/src/components';
import { Note } from '/src/lib/models';
import { CRUDMode, DataModelType } from '/src/lib/types';

export type EditPageProps = PropsWithChildren & {
  /**
   * Data model to edit
   */
  modelType: DataModelType;
  /**
   * Data model display name
   */
  modelName?: string;
  /**
   * Edits an instance of the selected data model
   * @returns void | Promise<void>
   */
  onEdit: () => void | Promise<void>;
  /**
   * Optional sidebar component to display
   */
  sidebar?: ReactNode;
  /**
   * Element ID
   */
  id?: string;
  /**
   * Sets the verbiage when talking about editing
   */
  editVerb?: string; // defaults to "Edit"
  /**
   * Display icon representing the data model to be edited
   */
  editIcon?: React.ReactNode;
  /**
   * Label for page's submit button
   */
  submitLabel?: string;
  /**
   * Display sidebar items
   */
  sidebarItems?: boolean[];
  /**
   * Keeps track of which sidebar items are open
   * @param items Sidebar items
   * @returns void
   */
  setSidebarItems?: (items: boolean[]) => void;
  /**
   * Flex value for sidebar
   */
  sidebarFlex?: string;
  /**
   * Flex value for page content
   */
  contentFlex?: string;
  /**
   * Page element width
   */
  width?: BoxProps['width'];
  /**
   * Config options for the Notes component
   */
  noteOptions?: {
    notes: Note[];
    onChange: (notes: Note[]) => void;
  };
  /**
   * Config options for the Documents component
   */
  documentOptions?: {
    files: string[];
    onChange: (files: string[]) => void;
  };
  /**
   * Flex value for Notes and Documents components
   */
  notesDocsFlex?: string;
};

/**
 * Edit Page Layout
 */
export const EditPage = ({ children, onEdit, editVerb = 'Edit', editIcon, ...props }: EditPageProps) => {
  return (
    <FormPage mode={CRUDMode.Edit} onSubmit={onEdit} verb={editVerb} icon={editIcon} {...props}>
      {children}
    </FormPage>
  );
};
